import React, { useState } from "react";

import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  Box,
  VStack,
  Icon,
  InputGroup,
  InputLeftElement,
  useToast,
} from "@chakra-ui/react";
import { FiEdit, FiFileText, FiTag } from "react-icons/fi";

import TeamSelect from "../../../../../components/select/TeamSelect";
import client from "../../../../../helpers/Client";
import SaveButton from "../../../../../components/buttons/SaveButton";
import TaskTypeSelect from "../../../../../shared/components/IssueTypeSelect.jsx";
export default function CreateTaskModal({
  ticketURL,
  resellerID,
  ticketRevalidator,
  state,
}) {
  const [title, setTitle] = useState(null);
  const [body, setBody] = useState(null);
  const [kind, setKind] = useState(null);
  const [team, setTeam] = useState(null);
  const [buttonLocked, setButtonLocked] = useState(false);
  const [errorData, setErrorData] = useState({});
  const toast = useToast();

  // Color mode values
  const bgColor = 'white';
  const borderColor = 'gray.200';
  const inputBg = 'gray.50';
  const inputFocusBg = 'white';
  const textColor = 'gray.800';
  const placeholderColor = 'gray.500';

  const handleCreate = () => {
    setButtonLocked(true);
    setErrorData({});
    const payload = { title, body, team, kind, ticket: ticketURL, user: null }
    client
      .post("/tasks/", payload)
      .then(() => {
        ticketRevalidator.revalidate();
        state.onClose();
        toast({
          title: "Görev başarıyla oluşturuldu",
          description: "Yeni görev kaydı eklendi.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      })
      .catch((error) => {
        if (error?.response?.data) {
          setErrorData(error.response.data);
        } else {
          setErrorData({"non_field_errors": ["Unknown error."]})
        }
        toast({
          title: "İşlem gerçekleştirilemiyor",
          description: "Lütfen bilgileri kontrol edip tekrar deneyin.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setButtonLocked(false);
      });
  };

  const handleClose = () => {
    setErrorData({});
    state.onClose();
  };

  return (
    <Modal isOpen={state.isOpen} onClose={state.onClose} size="lg">
      <ModalOverlay 
        bg="blackAlpha.300" 
        backdropFilter="blur(10px)" 
      />
      <ModalContent 
        bg={bgColor}
        borderRadius="20px"
        border="1px solid"
        borderColor={borderColor}
        boxShadow="xl"
        mx={4}
      >
        <Box
          bgGradient={'linear(135deg, blue.50 0%, purple.50 100%)'}
          borderTopRadius="20px"
          p={6}
        >
          <ModalHeader 
            p={0} 
            fontSize="2xl" 
            fontWeight="bold"
            color={textColor}
            display="flex"
            alignItems="center"
            gap={3}
          >
            <Icon as={FiEdit} boxSize={6} />
            Görev Oluştur
          </ModalHeader>
          <ModalCloseButton 
            onClick={handleClose}
            top={6} 
            right={6}
            bg="whiteAlpha.200"
            _hover={{ bg: 'whiteAlpha.300' }}
            borderRadius="full"
          />
        </Box>
        
        <ModalBody p={6}>
          <VStack spacing={5}>
            <FormControl isRequired isInvalid={errorData?.title}>
              <FormLabel color={textColor} fontWeight="semibold">Başlık</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiEdit} color={placeholderColor} />
                </InputLeftElement>
                <Input 
                  type="text" 
                  onChange={(e) => setTitle(e.target.value)}
                  bg={inputBg}
                  border="1px solid"
                  borderColor={borderColor}
                  color={textColor}
                  placeholder="Görev başlığını giriniz"
                  _placeholder={{ color: placeholderColor }}
                  _hover={{ borderColor: 'blue.300' }}
                  _focus={{
                    borderColor: 'blue.500',
                    bg: inputFocusBg,
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                  }}
                  borderRadius="12px"
                  h="48px"
                  pl="40px"
                />
              </InputGroup>
              {errorData.title &&
                errorData.title.map((e) => (
                  <FormErrorMessage key={e}>{e}</FormErrorMessage>
                ))}
            </FormControl>
            
            <FormControl isInvalid={errorData.body}>
              <FormLabel color={textColor} fontWeight="semibold">Açıklama</FormLabel>
              <Box position="relative">
                <Icon 
                  as={FiFileText} 
                  position="absolute" 
                  left={3} 
                  top={3} 
                  color={placeholderColor} 
                  zIndex={2}
                />
                <Textarea 
                  onChange={(e) => setBody(e.target.value)}
                  bg={inputBg}
                  border="1px solid"
                  borderColor={borderColor}
                  color={textColor}
                  placeholder="Görev açıklamasını giriniz"
                  _placeholder={{ color: placeholderColor }}
                  _hover={{ borderColor: 'blue.300' }}
                  _focus={{
                    borderColor: 'blue.500',
                    bg: inputFocusBg,
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                  }}
                  borderRadius="12px"
                  minH="100px"
                  pl="40px"
                  pt="12px"
                  resize="vertical"
                />
              </Box>
              {errorData.body &&
                errorData.body.map((e) => (
                  <FormErrorMessage key={e}>{e}</FormErrorMessage>
                ))}
            </FormControl>
            
            <FormControl isInvalid={errorData.kind}>
              <FormLabel color={textColor} fontWeight="semibold">Tip</FormLabel>
              <Box position="relative">
                <Icon 
                  as={FiTag} 
                  position="absolute" 
                  left={3} 
                  top="50%" 
                  transform="translateY(-50%)" 
                  color={placeholderColor} 
                  zIndex={2}
                />
                <TaskTypeSelect
                  option={kind}
                  setOption={setKind}
                  bg={inputBg}
                  border="1px solid"
                  borderColor={borderColor}
                  color={textColor}
                  borderRadius="12px"
                  h="48px"
                  pl={10}
                  _hover={{ borderColor: 'blue.300' }}
                  _focus={{
                    borderColor: 'blue.500',
                    bg: inputFocusBg,
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                  }}
                />
              </Box>
              {errorData.kind &&
                errorData.kind.map((e) => (
                  <FormErrorMessage key={e}>{e}</FormErrorMessage>
                ))}
            </FormControl>
          <TeamSelect
            {...{
              resellerID,
              team,
              setSelectedTeamURL: setTeam,
              errorData,
            }}
          />

          {errorData.non_field_errors &&
            errorData.non_field_errors.map((e) => (
              <Text bg="red.500" color={"white"} p={2} mt={2} key={e}>
                {e}
              </Text>
            ))}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={handleClose}>
            Vazgeç
          </Button>
          <SaveButton
            {...{
              isDisabled: title === null || buttonLocked || title.length === 0,
            }}
            onClick={handleCreate}
          >
            Kaydet
          </SaveButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
